import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Fab, IconButton, LinearProgress, MenuItem, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import {createUser, listUsers, resetUserPassword, deleteUser} from '../api/usersApi';
import {User, UserCreate} from '../types';
import {useAuth} from '../auth/AuthContext';
import {decodeRoles, getHighestRole} from '../auth/AuthUtils';

export default function UsersPage() {
  const { token } = useAuth();
  const role = useMemo(() => getHighestRole(decodeRoles(token)), [token]);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<{open: boolean; message: string; severity: 'success'|'error'|'info'}>({open: false, message: '', severity: 'success'});

  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState<UserCreate>({ username: '', password: '', role: 'READ' });
  const [creating, setCreating] = useState(false);

  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState<string>('');
  const [resetting, setResetting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setSnack({ open: true, message: 'Access denied: ADMIN role required', severity: 'error' });
      } else {
        setSnack({ open: true, message: 'Failed to load users', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const isAdminOrSuper = role === 'ADMIN' || role === 'SUPERADMIN';
  const isSuper = role === 'SUPERADMIN';

  function canManage(target: User) {
    if (isSuper) return true;
    if (role === 'ADMIN') return target.role === 'READ';
    return false;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '90%', mx: 'auto' }}>
      {loading && <LinearProgress />}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Users</Typography>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(u => (
            <TableRow key={u.id} hover>
              <TableCell>{u.username}</TableCell>
              <TableCell>{u.role}</TableCell>
              <TableCell>{u.enabled ? 'Yes' : 'No'}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Tooltip title={canManage(u) ? 'Reset password' : 'You cannot reset password for this user'}>
                    <span>
                      <IconButton size="small" onClick={() => { setResetUserId(u.id); setResetPassword(''); }} disabled={!canManage(u)}>
                        <KeyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canManage(u) ? 'Delete user' : 'You cannot delete this user'}>
                    <span>
                      <IconButton size="small" color="error" 
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to delete user ${u.username}?`)) {
                                      try {
                                        await deleteUser(u.id);
                                        setSnack({ open: true, message: 'User deleted', severity: 'success' });
                                        await load();
                                      } catch (err: any) {
                                        setSnack({ open: true, message: 'Failed to delete user', severity: 'error' });
                                      }
                                    }
                                  }} 
                                  disabled={!canManage(u)}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
          {users.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={4}>
                <Alert severity="info">No users found.</Alert>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Create User Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add new user</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Username" value={createForm.username} onChange={e => setCreateForm(f => ({...f, username: e.target.value}))} fullWidth />
            <TextField label="Password" type="password" value={createForm.password} onChange={e => setCreateForm(f => ({...f, password: e.target.value}))} fullWidth />
            <TextField select label="Role" value={createForm.role} onChange={e => setCreateForm(f => ({...f, role: e.target.value as any}))} fullWidth>
              <MenuItem value="READ">READ</MenuItem>
              {isSuper && <MenuItem value="ADMIN">ADMIN</MenuItem>}
              {isSuper && <MenuItem value="SUPERADMIN">SUPERADMIN</MenuItem>}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" disabled={creating || !createForm.username || !createForm.password || !createForm.role}
                  onClick={async () => {
                    try {
                      setCreating(true);
                      const created = await createUser(createForm);
                      setSnack({ open: true, message: `User ${created.username} created`, severity: 'success' });
                      setOpenCreate(false);
                      setCreateForm({ username: '', password: '', role: 'READ' });
                      await load();
                    } catch (err: any) {
                      const status = err?.response?.status;
                      if (status === 403) setSnack({ open: true, message: 'Access denied', severity: 'error' });
                      else setSnack({ open: true, message: 'Failed to create user', severity: 'error' });
                    } finally {
                      setCreating(false);
                    }
                  }}
          >Create</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetUserId} onClose={() => setResetUserId(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent dividers>
          <TextField label="New password" type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetUserId(null)} disabled={resetting}>Cancel</Button>
          <Button variant="contained" disabled={resetting || !resetPassword} onClick={async () => {
            if (!resetUserId) return;
            try {
              setResetting(true);
              await resetUserPassword(resetUserId, resetPassword);
              setSnack({ open: true, message: 'Password reset', severity: 'success' });
              setResetUserId(null);
              setResetPassword('');
            } catch (err: any) {
              const status = err?.response?.status;
              if (status === 403) setSnack({ open: true, message: 'Access denied', severity: 'error' });
              else setSnack({ open: true, message: 'Failed to reset password', severity: 'error' });
            } finally {
              setResetting(false);
            }
          }}>Reset</Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Tooltip title={isAdminOrSuper ? 'Add user' : 'Only ADMIN can add users'}>
        <span>
          <Fab color="primary" aria-label="add" onClick={() => setOpenCreate(true)} disabled={!isAdminOrSuper} sx={{ position: 'fixed', bottom: 24, right: 24 }}>
            <AddIcon />
          </Fab>
        </span>
      </Tooltip>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({...s, open: false}))} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnack(s => ({...s, open: false}))} severity={snack.severity as any} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
